import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, KeyRound, Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    // Hide reset page from search engines.
    useEffect(() => {
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow, noarchive";
        document.head.appendChild(meta);
        return () => { document.head.removeChild(meta); };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        setBusy(true);
        try {
            const r = await api.authResetPassword(token, password);
            setDone(true);
            toast.success("Password reset — sign in with your new password");
            // iter80.2 — Backend returns surface=platform|admin telling us
            // which collection this reset was for. Route the user back to
            // the correct login screen.
            const redirectTo = r?.surface === "platform" ? "/platform" : "/admin";
            setTimeout(() => navigate(redirectTo), 1500);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reset failed — link may be expired");
        }
        setBusy(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4">
            <div className="brutal-card p-7 lg:p-10 w-full max-w-md" data-testid="admin-reset-card">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center">
                        <Shield className="w-5 h-5 text-black" strokeWidth={2.5} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Site Admin</p>
                        <h1 className="font-display font-black text-2xl">Reset password</h1>
                    </div>
                </div>
                {!done ? (
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">New password</label>
                            <input
                                data-testid="admin-reset-pw-input"
                                type="password" value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="brutal-input w-full" autoFocus
                                placeholder="At least 8 characters"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Confirm</label>
                            <input
                                data-testid="admin-reset-pw-confirm-input"
                                type="password" value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="brutal-input w-full"
                            />
                        </div>
                        <button
                            data-testid="admin-reset-submit-btn"
                            type="submit" disabled={busy}
                            className="brutal-btn-primary w-full disabled:opacity-60"
                        >
                            {busy ? "Resetting..." : "Set new password"} <KeyRound className="w-4 h-4" />
                        </button>
                    </form>
                ) : (
                    <div className="text-center space-y-3" data-testid="admin-reset-success-state">
                        <div className="w-12 h-12 mx-auto bg-cyber border-2 border-black shadow-brutal flex items-center justify-center">
                            <Check className="w-6 h-6 text-black" strokeWidth={2.5} />
                        </div>
                        <h3 className="font-display font-black text-xl">Password updated</h3>
                        <p className="text-sm text-zinc-400">Redirecting to sign in…</p>
                        <button onClick={() => navigate("/admin")} className="brutal-btn-ghost text-xs">
                            Sign in now <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
